import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  ArrowLeft, Plus, Trash2, RefreshCw, FileText, CheckCircle2, Clock, XCircle,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + '/api' });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const CATEGORIES = [
  { value: 'UTILITY', label: 'Utility', hint: 'Order updates, appointment reminders, receipts' },
  { value: 'MARKETING', label: 'Marketing', hint: 'Promotions, offers, newsletters' },
  { value: 'AUTHENTICATION', label: 'Authentication', hint: 'OTPs and login codes' },
];

const LANGUAGES = ['en', 'en_US', 'en_GB', 'hi', 'mr', 'ta', 'te', 'kn'];

const StatusBadge = ({ status }) => {
  const map = {
    APPROVED:  { c: 'bg-emerald-100 text-emerald-700', I: CheckCircle2 },
    PENDING:   { c: 'bg-amber-100  text-amber-700',   I: Clock },
    REJECTED:  { c: 'bg-red-100    text-red-700',     I: XCircle },
    DISABLED:  { c: 'bg-gray-200   text-gray-700',    I: XCircle },
    PAUSED:    { c: 'bg-gray-200   text-gray-700',    I: Clock },
  };
  const cfg = map[status] || { c: 'bg-gray-100 text-gray-700', I: Info };
  const Icon = cfg.I;
  return <Badge className={`${cfg.c} text-[10px] gap-1`}><Icon className="w-3 h-3" /> {status || 'UNKNOWN'}</Badge>;
};

const CRMTemplatesPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    category: 'UTILITY',
    language: 'en',
    header_text: '',
    body_text: 'Hi {{1}}, this is a friendly reminder from Eden Skin Clinic.',
    footer_text: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/templates');
      setRows(res.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load templates');
      setRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const validate = () => {
    if (!/^[a-z0-9_]{3,100}$/.test(form.name)) {
      toast.error('Name must be lowercase snake_case (a-z, 0-9, _), 3-100 chars');
      return false;
    }
    if (!form.body_text.trim()) {
      toast.error('Body text is required');
      return false;
    }
    return true;
  };

  const create = async () => {
    if (!validate()) return;
    setCreating(true);
    try {
      // Extract example vars from {{1}} {{2}} etc. so Meta accepts our sample
      const varMatches = form.body_text.match(/\{\{\d+\}\}/g) || [];
      const example = varMatches.length > 0
        ? { body_text: [varMatches.map((_, i) => `sample_${i + 1}`)] }
        : undefined;

      const components = [];
      if (form.header_text.trim()) {
        components.push({ type: 'HEADER', format: 'TEXT', text: form.header_text.trim() });
      }
      components.push({
        type: 'BODY',
        text: form.body_text.trim(),
        ...(example ? { example } : {}),
      });
      if (form.footer_text.trim()) {
        components.push({ type: 'FOOTER', text: form.footer_text.trim() });
      }

      await api.post('/crm/templates', {
        name: form.name.trim(),
        category: form.category,
        language: form.language,
        components,
      });
      toast.success('Template submitted for Meta approval');
      setOpenCreate(false);
      setForm({ ...form, name: '', header_text: '', footer_text: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Meta rejected the template');
    } finally { setCreating(false); }
  };

  const del = async (name) => {
    if (!window.confirm(`Delete template "${name}"? This removes it from Meta.`)) return;
    try {
      await api.delete(`/crm/templates/${encodeURIComponent(name)}`);
      toast.success('Template deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const varCount = (form.body_text.match(/\{\{\d+\}\}/g) || []).length;

  if (user?.role !== 'Admin') {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-8 text-center text-gray-500">Admin only.</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/crm')} data-testid="tmpl-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Inbox
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" /> Meta Templates
              </h1>
              <p className="text-xs md:text-sm text-gray-500">Draft templates and submit them to Meta for approval.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="tmpl-refresh"><RefreshCw className="w-4 h-4" /></Button>
            <Button onClick={() => setOpenCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tmpl-create-btn">
              <Plus className="w-4 h-4 mr-1" /> Create template
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-0 divide-y" data-testid="tmpl-list">
            {loading && (
              <div className="p-6 text-center text-sm text-gray-400"><RefreshCw className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
            )}
            {!loading && rows.length === 0 && (
              <div className="p-8 text-center text-sm text-gray-400" data-testid="tmpl-empty">
                <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                No templates yet. Click <b>Create template</b> to draft your first one.
              </div>
            )}
            {rows.map((t) => {
              const body = (t.components || []).find((c) => c.type === 'BODY');
              return (
                <div key={t.id || t.name} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`tmpl-row-${t.name}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900 font-mono text-sm">{t.name}</p>
                      <StatusBadge status={t.status} />
                      <Badge className="bg-gray-100 text-gray-700 text-[10px]">{t.category}</Badge>
                      <Badge className="bg-gray-100 text-gray-700 text-[10px]">{t.language}</Badge>
                    </div>
                    {body?.text && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2 whitespace-pre-wrap">{body.text}</p>
                    )}
                    {t.rejected_reason && (
                      <p className="text-xs text-red-600 mt-1">Rejected: {t.rejected_reason}</p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => del(t.name)} className="text-red-600 hover:bg-red-50" data-testid={`tmpl-delete-${t.name}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tmpl-create-dialog">
          <DialogHeader>
            <DialogTitle>Create WhatsApp template</DialogTitle>
            <DialogDescription>
              Meta reviews templates within a few minutes to 24 hours. Use lowercase snake_case for the name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium">Template name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })}
                placeholder="e.g. appointment_reminder_v1" data-testid="tmpl-name" />
              <p className="text-[11px] text-gray-500 mt-0.5">Lowercase letters, digits, underscore.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full h-9 border rounded-md px-2 mt-1" data-testid="tmpl-category">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <p className="text-[11px] text-gray-500 mt-0.5">{CATEGORIES.find((c) => c.value === form.category)?.hint}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Language</label>
                <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
                  className="w-full h-9 border rounded-md px-2 mt-1" data-testid="tmpl-language">
                  {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Header (optional)</label>
              <Input value={form.header_text} onChange={(e) => setForm({ ...form, header_text: e.target.value })}
                placeholder="Eden Skin Clinic" data-testid="tmpl-header" />
            </div>
            <div>
              <label className="text-sm font-medium">Body <span className="text-red-500">*</span></label>
              <Textarea
                rows={5}
                value={form.body_text}
                onChange={(e) => setForm({ ...form, body_text: e.target.value })}
                placeholder="Hi {{1}}, your appointment is on {{2}} at {{3}}."
                data-testid="tmpl-body"
              />
              <p className="text-[11px] text-gray-500 mt-0.5">
                Use {'{{1}}, {{2}}'} for variables. Detected: <b>{varCount}</b> variable{varCount === 1 ? '' : 's'}.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Footer (optional)</label>
              <Input value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
                placeholder="Reply STOP to opt-out" data-testid="tmpl-footer" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button onClick={create} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tmpl-submit">
              {creating ? 'Submitting…' : 'Submit for approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CRMTemplatesPage;
